import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { unitUsageAPI } from '../../services/api';
import { downloadTemplate } from '../../utils/exportUtils';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import SuccessModal from '../Common/SuccessModal';
import ConfirmModal from '../Common/ConfirmModal';
import ErrorModal from '../Common/ErrorModal';
import {
  AdminPage,
  PageToolbar,
  ExportMenu,
  CardGrid,
  EntityCard,
  Pagination,
  useRecordActions,
} from '../Common/admin';

const PAGE_SIZE = 12;
const NS = 'unitUsage.list';

const UnitUsageList = () => {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmData, setConfirmData] = useState(null);
  const fileInputRef = React.useRef(null);

  const loadItems = useCallback(async () => {
    try {
      setLoading(true);
      const response = await unitUsageAPI.getAll(appliedSearch, page, PAGE_SIZE);
      setItems(response.data.items || []);
      setTotalPages(response.data.totalPages || 1);
      setTotalCount(response.data.totalCount ?? 0);
      setSelectedIds([]);
    } catch (err) {
      setErrorMessage(t(`${NS}.messages.loadError`));
      setShowErrorModal(true);
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [appliedSearch, page, t]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  // --- dialog-driven add / edit / details / delete ----------------------
  const formFields = useMemo(
    () => [
      {
        name: 'nameAr',
        label: t('unitUsage.create.fields.nameAr'),
        placeholder: t('unitUsage.create.fields.nameAr'),
        icon: 'fa-language',
        type: 'text',
        required: true,
      },
      {
        name: 'nameEng',
        label: t('unitUsage.create.fields.nameEng'),
        placeholder: t('unitUsage.create.fields.nameEng'),
        icon: 'fa-globe',
        type: 'text',
        required: true,
      },
      {
        name: 'isActive',
        label: t('unitUsage.create.fields.isActive'),
        placeholder: t('unitUsage.create.fields.isActive'),
        icon: 'fa-circle-check',
        type: 'checkbox',
        required: false,
      },
    ],
    [t]
  );

  const toDetails = useCallback(
    (record) => [
      { label: t('unitUsage.view.fields.nameAr'), value: record.nameAr },
      { label: t('unitUsage.view.fields.nameEng'), value: record.nameEng },
      { label: t('unitUsage.view.fields.isActive'), value: Boolean(record.isActive) },
    ],
    [t]
  );

  const actions = useRecordActions({
    api: unitUsageAPI,
    fields: formFields,
    labels: {
      create: t('unitUsage.create.title'),
      edit: t('unitUsage.edit.title'),
      details: t('unitUsage.view.title'),
      deleteConfirm: t(`${NS}.messages.deleteConfirm`),
    },
    toDetails,
    onChanged: loadItems,
    onError: (message) => {
      setErrorMessage(message);
      setShowErrorModal(true);
    },
  });

  const handleSearch = () => {
    setPage(1);
    setAppliedSearch(searchTerm);
  };

  const handleBulkDelete = () => {
    if (selectedIds.length === 0) {
      setErrorMessage(t(`${NS}.messages.selectToDelete`));
      setShowErrorModal(true);
      return;
    }

    setConfirmAction(() => async () => {
      try {
        const response = await unitUsageAPI.bulkDelete(selectedIds);
        setSuccessMessage(
          response.data.message ||
            t(`${NS}.messages.bulkDeleteSuccess`, { count: selectedIds.length })
        );
        setShowSuccessModal(true);
        setSelectedIds([]);
        loadItems();
      } catch (err) {
        setErrorMessage(
          err.response?.data?.message || t(`${NS}.messages.bulkDeleteError`)
        );
        setShowErrorModal(true);
      }
    });
    setConfirmData({
      title: t('common.confirm'),
      message: t(`${NS}.messages.bulkDeleteConfirm`, { count: selectedIds.length }),
      confirmText: t('common.delete'),
      cancelText: t('common.cancel'),
    });
    setShowConfirmModal(true);
  };

  const handleConfirm = async () => {
    setShowConfirmModal(false);
    if (confirmAction) await confirmAction();
    setConfirmAction(null);
    setConfirmData(null);
  };

  const handleCancel = () => {
    setShowConfirmModal(false);
    setConfirmAction(null);
    setConfirmData(null);
  };

  const handleSelectAll = (e) => {
    setSelectedIds(e.target.checked ? items.map((item) => item.id) : []);
  };

  const handleSelectOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleExport = async (format) => {
    try {
      let response;
      let filename;
      let contentType;
      const stamp = new Date().toISOString().slice(0, 10);

      switch (format) {
        case 'excel':
          response = await unitUsageAPI.exportExcel();
          filename = `UnitUsages_${stamp}.xlsx`;
          contentType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
          break;
        case 'csv':
          response = await unitUsageAPI.exportCsv();
          filename = `UnitUsages_${stamp}.csv`;
          contentType = 'text/csv;charset=utf-8';
          break;
        case 'pdf':
          response = await unitUsageAPI.exportPdf();
          filename = `UnitUsages_${stamp}.pdf`;
          contentType = 'application/pdf';
          break;
        default:
          return;
      }

      saveAs(new Blob([response.data], { type: contentType }), filename);
      setSuccessMessage(t(`${NS}.messages.exportSuccess`, { format: format.toUpperCase() }));
      setShowSuccessModal(true);
    } catch (err) {
      console.error('Export error:', err);
      setErrorMessage(t(`${NS}.messages.exportError`, { format: format.toUpperCase() }));
      setShowErrorModal(true);
    }
  };

  const handleDownloadTemplate = () => {
    downloadTemplate('UnitUsage');
    setSuccessMessage(t('common.templateDownloadSuccess'));
    setShowSuccessModal(true);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      setErrorMessage(t('common.importFileType'));
      setShowErrorModal(true);
      e.target.value = '';
      return;
    }

    try {
      const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array' });
      const rows = XLSX.utils.sheet_to_json(workbook.Sheets[workbook.SheetNames[0]]);

      const toImport = rows
        .map((row) => ({
          nameAr: row.NameAr || row.nameAr || '',
          nameEng: row.NameEng || row.nameEng || '',
          isActive:
            row.IsActive === undefined
              ? true
              : typeof row.IsActive === 'boolean'
              ? row.IsActive
              : row.IsActive === 'Yes' || row.IsActive === 'true',
        }))
        .filter((r) => r.nameAr && r.nameEng);

      if (toImport.length === 0) {
        setErrorMessage(t('common.importError'));
        setShowErrorModal(true);
        return;
      }

      let ok = 0;
      let failed = 0;
      for (const row of toImport) {
        try {
          await unitUsageAPI.create(row);
          ok += 1;
        } catch (err) {
          failed += 1;
          console.error('Error importing row:', row, err);
        }
      }

      if (ok > 0) {
        setSuccessMessage(t('common.importCount', { count: ok, failed }));
        setShowSuccessModal(true);
        loadItems();
      } else {
        setErrorMessage(t('common.importError'));
        setShowErrorModal(true);
      }
    } catch (err) {
      console.error('Import error:', err);
      setErrorMessage(t('common.importError'));
      setShowErrorModal(true);
    } finally {
      e.target.value = '';
    }
  };

  const toolbarActions = (
    <>
      <ExportMenu onExport={handleExport} />
      <button
        type="button"
        className="ad-icon-btn"
        title={t('common.import')}
        onClick={() => fileInputRef.current?.click()}
      >
        <i className="fas fa-file-import"></i>
      </button>
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls"
        style={{ display: 'none' }}
        onChange={handleFileUpload}
      />
      <button
        type="button"
        className="ad-icon-btn"
        title={t('common.downloadTemplate')}
        onClick={handleDownloadTemplate}
      >
        <i className="fas fa-download"></i>
      </button>
      <button
        type="button"
        className="ad-icon-btn ad-icon-btn-danger"
        title={t('common.bulkDelete')}
        onClick={handleBulkDelete}
        disabled={selectedIds.length === 0}
      >
        <i className="fas fa-trash"></i>
      </button>
    </>
  );

  return (
    <AdminPage title={t(`${NS}.title`)}>
      <PageToolbar
        onAdd={actions.openCreate}
        addLabel={t(`${NS}.addNew`)}
        searchValue={searchTerm}
        searchPlaceholder={t(`${NS}.searchPlaceholder`)}
        onSearchChange={setSearchTerm}
        onSearchSubmit={handleSearch}
        selectAll={{
          checked: selectedIds.length === items.length && items.length > 0,
          onChange: handleSelectAll,
          count: selectedIds.length,
        }}
        actions={toolbarActions}
      />

      <CardGrid
        loading={loading}
        isEmpty={items.length === 0}
        emptyTitle={t(`${NS}.messages.noData`)}
        emptyText={t('common.noResultsHint')}
        skeletonCount={PAGE_SIZE}
      >
        {items.map((item) => (
          <EntityCard
            key={item.id}
            title={item.nameAr}
            subtitle={item.nameEng}
            selected={selectedIds.includes(item.id)}
            onSelect={() => handleSelectOne(item.id)}
            fields={[
            ]}
            badges={[
              {
                label: item.isActive ? t('common.active') : t('common.inactive'),
                tone: item.isActive ? 'success' : 'danger',
              },
            ]}
            onView={() => actions.openDetails(item)}
            onEdit={() => actions.openEdit(item)}
            onDelete={() => actions.openDelete(item)}
          />
        ))}
      </CardGrid>

      {!loading && items.length > 0 && (
        <Pagination
          page={page}
          totalPages={totalPages}
          totalCount={totalCount}
          pageSize={PAGE_SIZE}
          onChange={setPage}
        />
      )}

      {actions.modals}

      <ErrorModal
        show={showErrorModal}
        message={errorMessage}
        onClose={() => setShowErrorModal(false)}
      />
      <SuccessModal
        show={showSuccessModal}
        message={successMessage}
        onClose={() => setShowSuccessModal(false)}
      />
      <ConfirmModal
        show={showConfirmModal}
        title={confirmData?.title}
        message={confirmData?.message}
        confirmText={confirmData?.confirmText}
        cancelText={confirmData?.cancelText}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </AdminPage>
  );
};

export default UnitUsageList;
